// <!-- GRACE:MODULE_START name="ui_landing" -->
// <!-- GRACE:DEPENDENCY target="ui_asr" -->
// <!-- GRACE:DEPENDENCY target="ui_gpt" -->
// <!-- GRACE:DEPENDENCY target="ui_templates" -->
// <!-- GRACE:DEPENDENCY target="ui_dialog" -->
// <!-- GRACE:DEPENDENCY target="ui_auth" -->
import { useState, useEffect } from "react";
import FactsPage from "./landing/FactsPage";
import TemplatePage from "./landing/TemplatePage";
import DialogPage from "./landing/DialogPage";
import useAudioRecorder from "../hooks/useAudioRecorder";
import { fetchTemplates, Template } from "../services/templateService";
import { Segment } from "../types/asyncProcessing";

interface ProcessingState {
  status: 'idle' | 'uploading' | 'processing' | 'completed' | 'error';
  progress: number;
  estimatedTime: number;
  operationId: string;
  error: string;
}

const DEFAULT_PROMPT = `Задача: из сырого текста, полученного распознаванием аудио, извлечь только факты и тезисы.

Правила:
1) Не делай выводов, интерпретаций и рекомендаций.
2) Ничего не придумывай и не дополняй; при нехватке данных ставь тег [?].
3) Числа пиши цифрами; единицы измерения, диапазоны и проценты сохраняй как в тексте (напр., 10–12, 5%).
4) Даты нормализуй в формате ДД.ММ.ГГГГ, если однозначно; иначе оставь исходно и пометь [?].
5) Игнорируй приветствия, эмоции, оценочные эпитеты, слова-паразиты, оговорки и служебные фразы.
6) Объединяй дубликаты; сохраняй порядок появления в тексте.
7) Исправляй только явные опечатки без изменения смысла; не восстанавливай пропуски.
8) Выводи строго по шаблону ниже; никаких дополнительных разделов и комментариев.
9) Если раздел пуст, выведи одиночный дефис «-».

Формат вывода (Markdown строго по шаблону):

# Факты
- <краткая формулировка факта из текста; при сомнении добавь [?]>

# Тезисы
- <краткая формулировка тезиса; без оценок и советов; при сомнении [?]>
`;

const initialProcessingState: ProcessingState = {
  status: 'idle',
  progress: 0,
  estimatedTime: 0,
  operationId: '',
  error: ''
};

// Превращает массив сегментов диаризации в редактируемый текст с префиксами "Спикер N: ".
// Нумерация назначается по порядку первого появления спикера в записи.
function segmentsToLabeledText(segments: Segment[]): string {
  const speakerNumberMap = new Map<string, number>();
  let nextNum = 1;
  return segments
    .map((s) => {
      if (!speakerNumberMap.has(s.speaker)) {
        speakerNumberMap.set(s.speaker, nextNum++);
      }
      return `Спикер ${speakerNumberMap.get(s.speaker)}: ${s.text}`;
    })
    .join('\n');
}

// <LLM_NOTE>
// gptService и templateService импортируются ДИНАМИЧЕСКИ (lazy import) внутри processWithGPT()
// и formatByTemplate(). Это code splitting — сервисы грузятся только при первом использовании.
// Побочный эффект: ошибки импорта проявляются только при первом вызове, не при монтировании.
//
// Hash-routing управляет тремя режимами: 'facts' (#facts, дефолт), 'template' (#template),
// 'dialog' (#dialog — распознавание с диаризацией, доступно только по прямой ссылке).
// Для dialog используется ОТДЕЛЬНЫЙ экземпляр useAudioRecorder с diarize=true,
// иначе состояние записи и транскрипции пересекалось бы между страницами.
// </LLM_NOTE>
// <!-- GRACE:CONTRACT function="LandingOnlyApp" -->
export default function LandingOnlyApp() {
  // State
  const [currentPage, setCurrentPage] = useState<'facts' | 'template' | 'dialog'>('facts');

  // ── Состояние страницы facts/template (общий recorder через /api/speechkit/upload) ──
  const [transcription, setTranscription] = useState("");
  const [processingState, setProcessingState] = useState<ProcessingState>(initialProcessingState);

  const audioRecorder = useAudioRecorder({
    onTranscription: (text) => {
      setTranscription(text);
      setProcessingState(prev => ({ ...prev, status: 'completed' }));
    },
    onProcessingStart: () => {
      setProcessingState(prev => ({ ...prev, status: 'processing', error: '' }));
    },
    onProcessingEnd: () => {
      // Status will be set by onTranscription or onOperationError
    },
    onUploadStart: () => {
      setProcessingState(prev => ({ ...prev, status: 'uploading', progress: 0 }));
    },
    onUploadProgress: (progress) => {
      setProcessingState(prev => ({ ...prev, progress }));
    },
    onOperationStart: (operationId) => {
      setProcessingState(prev => ({ ...prev, operationId, status: 'processing' }));
    },
    onOperationProgress: (progress, estimatedTime) => {
      setProcessingState(prev => ({ ...prev, progress, estimatedTime }));
    },
    onOperationError: (error) => {
      setProcessingState(prev => ({ ...prev, status: 'error', error }));
    }
  });

  // ── Состояние страницы dialog (async /api/speechkit/recognize/async + polling, diarize=true) ──
  const [dialogTranscription, setDialogTranscription] = useState("");
  const [dialogProcessingState, setDialogProcessingState] = useState<ProcessingState>(initialProcessingState);

  const DIALOG_OPERATION_KEY = 'dialog_recognition_operation_id';

  const dialogRecorder = useAudioRecorder({
    diarize: true,
    asyncMode: true,
    suppressMockFallback: true,
    operationStorageKey: DIALOG_OPERATION_KEY,
    onSegments: (segments) => {
      // Если backend вернул сегменты — заменяем plain-text на лейблы спикеров.
      setDialogTranscription(segmentsToLabeledText(segments));
    },
    onTranscription: (text) => {
      // Fallback: если segments отсутствуют (mock_mode/ASR_PROVIDER=speechkit) — кладём plain text.
      // При наличии segments этот вызов перезапишется отформатированной версией только если
      // он пришёл РАНЬШЕ onSegments. По текущему контракту хук вызывает onSegments первым.
      setDialogTranscription((prev) => prev || text);
      setDialogProcessingState(prev => ({ ...prev, status: 'completed' }));
    },
    onProcessingStart: () => {
      setDialogTranscription("");
      setDialogProcessingState(prev => ({ ...prev, status: 'processing', error: '' }));
    },
    onProcessingEnd: () => { /* see onTranscription / onOperationError */ },
    onUploadStart: () => {
      setDialogProcessingState(prev => ({ ...prev, status: 'uploading', progress: 0 }));
    },
    onUploadProgress: (progress) => {
      setDialogProcessingState(prev => ({ ...prev, progress }));
    },
    onOperationStart: (operationId) => {
      setDialogProcessingState(prev => ({ ...prev, operationId, status: 'processing' }));
    },
    onOperationProgress: (progress, estimatedTime, stage) => {
      setDialogProcessingState(prev => ({
        ...prev,
        progress,
        estimatedTime,
        stage,
        status: 'processing',
      }));
    },
    onOperationError: (error) => {
      setDialogProcessingState(prev => ({ ...prev, status: 'error', error }));
    }
  });

  // ── GPT (общий промпт-стейт; результаты раздельны для facts и dialog) ──
  const [prompt, setPrompt] = useState(DEFAULT_PROMPT);
  const [gptResult, setGptResult] = useState("");
  const [gptProcessing, setGptProcessing] = useState(false);

  const [dialogPrompt, setDialogPrompt] = useState(DEFAULT_PROMPT);
  const [dialogGptResult, setDialogGptResult] = useState("");
  const [dialogGptProcessing, setDialogGptProcessing] = useState(false);

  // Templates
  const [templates, setTemplates] = useState<Template[]>([]);
  const [templatesLoading, setTemplatesLoading] = useState(false);
  const [selectedTpl, setSelectedTpl] = useState<string>("");
  const [formattedResult, setFormattedResult] = useState("");
  const [formatProcessing, setFormatProcessing] = useState(false);

  // Load templates from backend on mount
  useEffect(() => {
    const loadTemplates = async () => {
      setTemplatesLoading(true);
      try {
        const loadedTemplates = await fetchTemplates();
        setTemplates(loadedTemplates);
        // Select first template by default
        if (loadedTemplates.length > 0) {
          setSelectedTpl(loadedTemplates[0].id);
        }
      } catch (error) {
        console.error('Failed to load templates:', error);
        // Set empty templates on error, user will see empty dropdown
      } finally {
        setTemplatesLoading(false);
      }
    };

    loadTemplates();
  }, []);

  // Hash-routing: синхронизация с URL
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash.slice(1); // Убираем #
      if (hash === 'template') {
        setCurrentPage('template');
      } else if (hash === 'dialog') {
        setCurrentPage('dialog');
      } else {
        setCurrentPage('facts');
      }
    };

    // Установить начальную страницу на основе hash
    handleHashChange();

    // Слушать изменения hash
    window.addEventListener('hashchange', handleHashChange);
    return () => window.removeEventListener('hashchange', handleHashChange);
  }, []);

  // Возобновить polling после перезагрузки страницы на #dialog
  useEffect(() => {
    if (currentPage !== 'dialog') {
      return;
    }
    void dialogRecorder.resumePendingOperation();
  }, [currentPage, dialogRecorder.resumePendingOperation]);

  // Обновление hash при смене страницы
  const handleNavigate = (page: 'facts' | 'template' | 'dialog') => {
    setCurrentPage(page);
    window.location.hash = page;
  };

  // Helper functions
  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch (err) {
      console.error('Failed to copy:', err);
    }
  };

  const downloadText = (text: string, filename: string) => {
    const blob = new Blob([text], { type: "text/plain;charset=utf-8" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleFileChange = (file: File) => {
    audioRecorder.processAudioFile(file);
  };

  const handleStartRecording = async () => {
    await audioRecorder.startRecording();
  };

  const handleStopRecording = () => {
    audioRecorder.stopRecording();
  };

  const handleDialogFileChange = (file: File) => {
    dialogRecorder.processAudioFile(file);
  };

  const handleDialogStartRecording = async () => {
    await dialogRecorder.startRecording();
  };

  const handleDialogStopRecording = () => {
    dialogRecorder.stopRecording();
  };

  // Универсальный SSE-вызов GPT. Принимает источник, промпт и сеттер результата.
  const processWithGPTSource = async (
    source: string,
    promptText: string,
    setResult: (text: string) => void,
    setProcessing: (busy: boolean) => void,
  ) => {
    if (!source.trim()) {
      setResult("");
      return;
    }

    setProcessing(true);
    setResult("");

    try {
      const { processTextStream } = await import("../services/gptService");

      let accumulatedResult = "";

      await processTextStream(source, promptText, {
        onChunk: (chunk) => {
          accumulatedResult += chunk;
          setResult(accumulatedResult);
        },
        onComplete: (processingTime) => {
          console.log(`GPT processing completed in ${processingTime}s`);
          setProcessing(false);
        },
        onError: (error) => {
          console.error('GPT processing error:', error);
          setResult(`Ошибка обработки: ${error}`);
          setProcessing(false);
        }
      });
    } catch (error) {
      console.error('Failed to process with GPT:', error);
      setResult(`Ошибка: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
      setProcessing(false);
    }
  };

  const processWithGPT = () =>
    processWithGPTSource(transcription, prompt, setGptResult, setGptProcessing);

  const processDialogWithGPT = () =>
    processWithGPTSource(dialogTranscription, dialogPrompt, setDialogGptResult, setDialogGptProcessing);

  const formatByTemplate = async () => {
    const base = gptResult || transcription || "";
    if (!base || !selectedTpl) {
      setFormattedResult("");
      return;
    }

    setFormatProcessing(true);

    try {
      // Import template service
      const { formatTextByTemplate } = await import("../services/templateService");

      const result = await formatTextByTemplate(base, selectedTpl);
      setFormattedResult(result.formatted_text);
      console.log(`Template formatting completed in ${result.processing_time}s`);
    } catch (error) {
      console.error('Failed to format by template:', error);
      setFormattedResult(`Ошибка форматирования: ${error instanceof Error ? error.message : 'Неизвестная ошибка'}`);
    } finally {
      setFormatProcessing(false);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTpl(templateId);
  };

  return (
    <div className="min-h-screen bg-gray-50 text-gray-900">
      <main className="mx-auto max-w-7xl px-4 py-6">
        {currentPage === 'facts' ? (
          <FactsPage
            onNavigate={handleNavigate}
            isRecording={audioRecorder.isRecording}
            transcription={transcription}
            processingState={processingState}
            audioUrl={audioRecorder.audioUrl}
            audioBlob={audioRecorder.audioBlob}
            onStartRecording={handleStartRecording}
            onStopRecording={handleStopRecording}
            onTranscriptionChange={setTranscription}
            onFileChange={handleFileChange}
            onClearTranscription={() => setTranscription("")}
            onCopyTranscription={() => copyToClipboard(transcription)}
            onDownloadTranscription={() => downloadText(transcription, "transcription.txt")}
            prompt={prompt}
            gptResult={gptResult}
            gptProcessing={gptProcessing}
            onPromptChange={setPrompt}
            onProcessWithGPT={processWithGPT}
            onCopyGPTResult={() => copyToClipboard(gptResult)}
            onDownloadGPTResult={() => downloadText(gptResult, "gpt-result.txt")}
          />
        ) : currentPage === 'dialog' ? (
          <DialogPage
            onNavigate={handleNavigate}
            isRecording={dialogRecorder.isRecording}
            transcription={dialogTranscription}
            processingState={dialogProcessingState}
            audioUrl={dialogRecorder.audioUrl}
            audioBlob={dialogRecorder.audioBlob}
            onStartRecording={handleDialogStartRecording}
            onStopRecording={handleDialogStopRecording}
            onTranscriptionChange={setDialogTranscription}
            onFileChange={handleDialogFileChange}
            onClearTranscription={() => setDialogTranscription("")}
            onCopyTranscription={() => copyToClipboard(dialogTranscription)}
            onDownloadTranscription={() => downloadText(dialogTranscription, "dialog.txt")}
            prompt={dialogPrompt}
            gptResult={dialogGptResult}
            gptProcessing={dialogGptProcessing}
            onPromptChange={setDialogPrompt}
            onProcessWithGPT={processDialogWithGPT}
            onCopyGPTResult={() => copyToClipboard(dialogGptResult)}
            onDownloadGPTResult={() => downloadText(dialogGptResult, "dialog-gpt-result.txt")}
          />
        ) : (
          <TemplatePage
            onNavigate={handleNavigate}
            isRecording={audioRecorder.isRecording}
            transcription={transcription}
            processingState={processingState}
            audioUrl={audioRecorder.audioUrl}
            audioBlob={audioRecorder.audioBlob}
            onStartRecording={handleStartRecording}
            onStopRecording={handleStopRecording}
            onTranscriptionChange={setTranscription}
            onFileChange={handleFileChange}
            onClearTranscription={() => setTranscription("")}
            onCopyTranscription={() => copyToClipboard(transcription)}
            onDownloadTranscription={() => downloadText(transcription, "transcription.txt")}
            templates={templates}
            templatesLoading={templatesLoading}
            selectedTemplate={selectedTpl}
            formattedResult={formattedResult}
            formatProcessing={formatProcessing}
            onTemplateSelect={handleTemplateSelect}
            onFormatByTemplate={formatByTemplate}
            onCopyFormattedResult={() => copyToClipboard(formattedResult)}
            onDownloadFormattedResult={() => downloadText(formattedResult, "formatted-result.txt")}
          />
        )}
      </main>
    </div>
  );
}
// <!-- GRACE:MODULE_END name="ui_landing" -->
